"use client";

import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { RefObject, useEffect, useRef, useState } from "react";
import { Globe, MessageCircle, FileText, BookOpen, Lightbulb, Brain } from "lucide-react";

interface AgenticMemoryDemoProps {
  containerRef: RefObject<HTMLDivElement>;
  onShowDownloadChange?: (show: boolean) => void;
}

export function AgenticMemoryDemo({ containerRef, onShowDownloadChange }: AgenticMemoryDemoProps) {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end end"],
  });

  // 视频播放状态
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // 基于滚动进度控制视频显示 - 第二阶段 (0.7-1.0, 占总进度30%)
  const showVideo = useTransform(scrollYProgress, [0.7, 0.75, 0.9, 1.0], [0, 1, 1, 1]);

  // 视频播放控制 - 基于 showVideo motion value
  useEffect(() => {
    const unsubscribe = showVideo.on("change", (value) => {
      const shouldPlay = value > 0;

      if (videoRef.current) {
        if (shouldPlay && !videoPlaying) {
          videoRef.current.currentTime = 0;
          videoRef.current.play();
          setVideoPlaying(true);
        } else if (!shouldPlay && videoPlaying) {
          videoRef.current.pause();
          setVideoPlaying(false);
        }
      }

      // 通知父组件视频状态变化
      if (onShowDownloadChange) {
        onShowDownloadChange(shouldPlay);
      }
    });

    return () => unsubscribe();
  }, [videoPlaying, onShowDownloadChange, showVideo]);

  // Animation transforms for memory connection workflow - 扩展到0.7并添加右侧退场
  // Container appears (0-0.08) - 扩展时间
  const containerOpacity = useTransform(scrollYProgress, [0, 0.08], [0, 1]);
  const containerScale = useTransform(scrollYProgress, [0, 0.08], [0.95, 1]);

  // Data sources appear (0.08-0.3) - 扩展时间
  const browserHistoryY = useTransform(scrollYProgress, [0.08, 0.12], [-30, 0]);
  const browserHistoryAppearOpacity = useTransform(scrollYProgress, [0.08, 0.12], [0, 1]);

  const chatHistoryY = useTransform(scrollYProgress, [0.12, 0.16], [-30, 0]);
  const chatHistoryAppearOpacity = useTransform(scrollYProgress, [0.12, 0.16], [0, 1]);

  const pageDataY = useTransform(scrollYProgress, [0.16, 0.2], [-30, 0]);
  const pageDataAppearOpacity = useTransform(scrollYProgress, [0.16, 0.2], [0, 1]);

  const knowledgeBaseY = useTransform(scrollYProgress, [0.2, 0.24], [-30, 0]);
  const knowledgeBaseAppearOpacity = useTransform(scrollYProgress, [0.2, 0.24], [0, 1]);

  // Connection lines appear (0.3-0.45) - 扩展时间
  const connectionProgress = useTransform(scrollYProgress, [0.3, 0.45], [0, 1]);

  // Connecting text animation (0.4-0.55) - 扩展时间
  const connectingProgress = useTransform(scrollYProgress, [0.4, 0.55], [0, 1]);

  // Memory brain center appears (0.55-0.6) - 调整时机
  const memoryBrainAppearOpacity = useTransform(scrollYProgress, [0.55, 0.6], [0, 1]);
  const memoryBrainScale = useTransform(scrollYProgress, [0.55, 0.6], [0.8, 1]);

  // Proactive suggestions appear (0.6-0.65) - 调整时机
  const suggestionsAppearOpacity = useTransform(scrollYProgress, [0.6, 0.65], [0, 1]);
  const suggestionsY = useTransform(scrollYProgress, [0.6, 0.65], [20, 0]);

  // Sequential exit from inside to outside (0.65-0.7) - 从内到外依次右侧退场
  // 1. Memory brain exits first (center)
  const memoryBrainExitX = useTransform(scrollYProgress, [0.65, 0.66], [0, 200]);
  const memoryBrainExitOpacity = useTransform(scrollYProgress, [0.65, 0.66], [1, 0]);

  // 2. Connecting text exits
  const connectingExitX = useTransform(scrollYProgress, [0.66, 0.67], [0, 200]);
  const connectingExitOpacity = useTransform(scrollYProgress, [0.66, 0.67], [1, 0]);

  // 3. Connection lines fade out
  const connectionExitOpacity = useTransform(scrollYProgress, [0.67, 0.68], [1, 0]);

  // 4. Data sources exit (corners)
  const browserHistoryExitX = useTransform(scrollYProgress, [0.68, 0.69], [0, 200]);
  const browserHistoryExitOpacity = useTransform(scrollYProgress, [0.68, 0.69], [1, 0]);
  const chatHistoryExitX = useTransform(scrollYProgress, [0.68, 0.69], [0, 200]);
  const chatHistoryExitOpacity = useTransform(scrollYProgress, [0.68, 0.69], [1, 0]);
  const pageDataExitX = useTransform(scrollYProgress, [0.68, 0.69], [0, 200]);
  const pageDataExitOpacity = useTransform(scrollYProgress, [0.68, 0.69], [1, 0]);
  const knowledgeBaseExitX = useTransform(scrollYProgress, [0.68, 0.69], [0, 200]);
  const knowledgeBaseExitOpacity = useTransform(scrollYProgress, [0.68, 0.69], [1, 0]);

  // 5. Suggestions exit last
  const suggestionsExitX = useTransform(scrollYProgress, [0.69, 0.7], [0, 200]);
  const suggestionsExitOpacity = useTransform(scrollYProgress, [0.69, 0.7], [1, 0]);

  // Combined opacity transforms
  const combinedBrowserHistoryOpacity = useTransform(
    [browserHistoryAppearOpacity, browserHistoryExitOpacity],
    ([appear, exit]) => Math.min(appear as number, exit as number)
  );
  const combinedChatHistoryOpacity = useTransform(
    [chatHistoryAppearOpacity, chatHistoryExitOpacity],
    ([appear, exit]) => Math.min(appear as number, exit as number)
  );
  const combinedPageDataOpacity = useTransform([pageDataAppearOpacity, pageDataExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedKnowledgeBaseOpacity = useTransform(
    [knowledgeBaseAppearOpacity, knowledgeBaseExitOpacity],
    ([appear, exit]) => Math.min(appear as number, exit as number)
  );
  const combinedMemoryBrainOpacity = useTransform(
    [memoryBrainAppearOpacity, memoryBrainExitOpacity],
    ([appear, exit]) => Math.min(appear as number, exit as number)
  );
  const combinedSuggestionsOpacity = useTransform(
    [suggestionsAppearOpacity, suggestionsExitOpacity],
    ([appear, exit]) => Math.min(appear as number, exit as number)
  );
  const combinedConnectingOpacity = useTransform([connectingProgress, connectingExitOpacity], ([appear, exit]) =>
    Math.min(appear as number, exit as number)
  );
  const combinedConnectionOpacity = useTransform([connectionProgress, connectionExitOpacity], ([progress, exit]) =>
    Math.min(progress as number, exit as number)
  );

  return (
    <div className="w-full relative flex items-center justify-center">
      {/* 视频容器 */}
      <motion.div
        className="absolute inset-0 z-20 flex items-center justify-center"
        style={{
          opacity: showVideo,
          x: useTransform(showVideo, [0, 1], [-100, 0]),
          filter: useTransform(showVideo, [0, 1], ["blur(4px)", "blur(0px)"]),
        }}
      >
        <video
          ref={videoRef}
          className="w-full h-full object-cover rounded-2xl"
          src="https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+main+page+animation/Agentic+Memory.webm"
          loop
          muted
          playsInline
        />
      </motion.div>

      {/* Demo Container */}
      <motion.div
        className="w-[700px] h-[500px] relative overflow-hidden"
        style={{
          opacity: useTransform(showVideo, [0, 0.1, 0.5, 1], [1, 1, 0.3, 0]),
        }}
        transition={{ duration: 0.5 }}
      >
        {/* Main Container */}
        <motion.div
          className="w-full h-full flex items-center justify-center"
          style={{
            opacity: containerOpacity,
            scale: containerScale,
          }}
        >
          <div className="w-[700px] h-[500px] bg-white/10 rounded-2xl border border-white/10 p-6 relative overflow-hidden">
            {/* Header */}
            <div className="h-8 bg-white/10 rounded-lg w-48 mb-6"></div>

            {/* Data Sources - Four corners with lucide icons */}
            {/* Browser History - Top Left */}
            <motion.div
              className="absolute top-16 left-6"
              style={{
                x: browserHistoryExitX,
                y: browserHistoryY,
                opacity: combinedBrowserHistoryOpacity,
              }}
            >
              <div className="w-20 h-16 bg-white/10 rounded-xl border border-white/10 flex items-center justify-center">
                <Globe className="w-8 h-8 text-white/70" />
              </div>
            </motion.div>

            {/* Chat History - Top Right */}
            <motion.div
              className="absolute top-16 right-6"
              style={{
                x: chatHistoryExitX,
                y: chatHistoryY,
                opacity: combinedChatHistoryOpacity,
              }}
            >
              <div className="w-20 h-16 bg-white/10 rounded-xl border border-white/10 flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-white/70" />
              </div>
            </motion.div>

            {/* Page Data - Bottom Left */}
            <motion.div
              className="absolute bottom-16 left-6"
              style={{
                x: pageDataExitX,
                y: pageDataY,
                opacity: combinedPageDataOpacity,
              }}
            >
              <div className="w-20 h-16 bg-white/10 rounded-xl border border-white/10 flex items-center justify-center">
                <FileText className="w-8 h-8 text-white/70" />
              </div>
            </motion.div>

            {/* Knowledge Base - Bottom Right */}
            <motion.div
              className="absolute bottom-16 right-6"
              style={{
                x: knowledgeBaseExitX,
                y: knowledgeBaseY,
                opacity: combinedKnowledgeBaseOpacity,
              }}
            >
              <div className="w-20 h-16 bg-white/10 rounded-xl border border-white/10 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-white/70" />
              </div>
            </motion.div>

            {/* Connection Lines with rounded corners */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 600 450">
              {/* Top Left: Right then down with rounded corner */}
              <motion.path
                d="M 80 90 L 200 90 Q 220 90 220 110 L 220 200 Q 220 220 240 220 L 300 220"
                stroke="rgba(255, 255, 255, 0.4)"
                strokeWidth="2"
                fill="none"
                opacity="0.6"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                style={{
                  pathLength: connectionProgress,
                  opacity: combinedConnectionOpacity,
                }}
              />

              {/* Top Right: Left then down with rounded corner */}
              <motion.path
                d="M 520 90 L 400 90 Q 380 90 380 110 L 380 200 Q 380 220 360 220 L 300 220"
                stroke="rgba(255, 255, 255, 0.4)"
                strokeWidth="2"
                fill="none"
                opacity="0.6"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                style={{
                  pathLength: connectionProgress,
                  opacity: combinedConnectionOpacity,
                }}
              />

              {/* Bottom Left: Right then up with rounded corner */}
              <motion.path
                d="M 80 360 L 200 360 Q 220 360 220 340 L 220 250 Q 220 230 240 230 L 300 230"
                stroke="rgba(255, 255, 255, 0.4)"
                strokeWidth="2"
                fill="none"
                opacity="0.6"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                style={{
                  pathLength: connectionProgress,
                  opacity: combinedConnectionOpacity,
                }}
              />

              {/* Bottom Right: Left then up with rounded corner */}
              <motion.path
                d="M 520 360 L 400 360 Q 380 360 380 340 L 380 250 Q 380 230 360 230 L 300 230"
                stroke="rgba(255, 255, 255, 0.4)"
                strokeWidth="2"
                fill="none"
                opacity="0.6"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                style={{
                  pathLength: connectionProgress,
                  opacity: combinedConnectionOpacity,
                }}
              />
            </svg>

            {/* Memory Brain Center */}
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{
                x: memoryBrainExitX,
                opacity: combinedMemoryBrainOpacity,
                scale: memoryBrainScale,
              }}
            >
              <div className="w-16 h-12 bg-white/10 rounded-lg border-2 border-white/10 flex items-center justify-center">
                <Brain className="w-8 h-8 text-white/70 animate-pulse" />
              </div>
            </motion.div>

            {/* Connecting Status */}
            <motion.div
              className="absolute top-16 left-1/2 -translate-x-1/2"
              style={{
                x: connectingExitX,
                opacity: combinedConnectingOpacity,
              }}
            >
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2 border border-white/10">
                <div className="w-2 h-2 bg-white/70 rounded-full animate-pulse"></div>
                <div className="text-sm text-white/70 font-mono">Connecting...</div>
              </div>
            </motion.div>

            {/* Proactive Suggestions - Icon based */}
            <motion.div
              className="absolute bottom-6 left-1/2 -translate-x-1/2"
              style={{
                x: suggestionsExitX,
                y: suggestionsY,
                opacity: combinedSuggestionsOpacity,
              }}
            >
              <div className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-3 border border-white/10">
                <Lightbulb className="w-5 h-5 text-white/70" />
                <div className="text-sm text-white/70">Personalized insights ready</div>
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-white/70 rounded-full"></div>
                  <div className="w-2 h-2 bg-white/70 rounded-full"></div>
                  <div className="w-2 h-2 bg-white/70 rounded-full"></div>
                </div>
              </div>
            </motion.div>

            {/* Background pattern dots */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-8 right-8 w-12 h-12 border border-white/10 rounded-full"></div>
              <div className="absolute bottom-12 left-12 w-6 h-6 border border-white/10 rounded"></div>
              <div className="absolute top-1/2 right-16 w-4 h-4 border border-white/10 rounded-full"></div>
              <div className="absolute bottom-1/3 left-1/4 w-8 h-8 border border-white/10 rounded-lg"></div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
